import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { useBrand } from '../context/BrandContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const Login = () => {
  const { brandName } = useBrand();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('login'); // login | phone | 2fa
  const [phone, setPhone] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [demoAvailability, setDemoAvailability] = useState(null);  // { available, remaining_seconds, in_use }
  const [demoExpired, setDemoExpired] = useState(false);

  // Pick up ?demo_expired=1 / ?demo_ended=1 query flags (set on auto-logout / manual end)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('demo_expired') === '1' || params.get('demo_ended') === '1') {
      setDemoExpired(true);
    }
  }, []);

  // Poll demo availability every 10s
  useEffect(() => {
    let cancelled = false;
    const fetchAvailability = async () => {
      try {
        const res = await axios.get(`${API_BASE}/demo/availability`);
        if (!cancelled) setDemoAvailability(res.data);
      } catch { /* silent */ }
    };
    fetchAvailability();
    const id = setInterval(fetchAvailability, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const fillDemoCreds = () => {
    setEmail('Demo@demo.com');
    setPassword('Demo123');
  };

  const fmtRemaining = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  // Lock login entirely when a demo is in progress (single-user demo deployment).
  const demoInUse = demoAvailability?.in_use && demoAvailability?.remaining_seconds > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = { email, password };

      if (step === 'phone') {
        // Submitting phone number to save + send code
        payload.phone = phone;
      }

      if (step === '2fa') {
        payload.two_fa_code = twoFACode;
      }

      const response = await axios.post(`${API_BASE}/auth/login`, payload);

      if (response.data.needs_phone) {
        // No phone on file — prompt to add
        setStep('phone');
        setStatusMessage(response.data.message);
        setLoading(false);
        return;
      }

      if (response.data.requires_2fa) {
        // Code sent — enter it
        setStep('2fa');
        setStatusMessage(response.data.message);
        setLoading(false);
        return;
      }

      if (response.data.success) {
        const { user, token } = response.data;
        localStorage.setItem('mcaToken', token);
        localStorage.setItem('mcaUser', JSON.stringify(user));
        sessionStorage.setItem('mcaToken', token);
        sessionStorage.setItem('mcaUser', JSON.stringify(user));
        window.location.replace('/funded-deals');
      } else {
        setError('Login failed');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep('login');
    setTwoFACode('');
    setPhone('');
    setStatusMessage('');
    setError('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-orange-100">
      <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-orange-500 text-white rounded-2xl text-2xl font-bold mb-4">
            {(brandName || 'D').trim().charAt(0).toUpperCase()}
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2" data-testid="login-brand-name">{brandName}</h1>
          <p className="text-gray-600">
            {step === 'login' && 'Sign in to your account'}
            {step === 'phone' && 'Add your phone number'}
            {step === '2fa' && 'Enter verification code'}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
          </div>
        )}

        {statusMessage && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-blue-700 text-sm">
            {statusMessage}
          </div>
        )}

        {/* Demo session widget (login step only) */}
        {step === 'login' && (
          <div data-testid="demo-callout" className="mb-6 p-4 rounded-lg border-2 border-orange-300 bg-gradient-to-br from-orange-50 to-amber-50">
            {demoExpired && (
              <div className="mb-3 p-2 rounded bg-emerald-100 text-emerald-800 text-xs font-medium text-center">
                ✨ Thanks for trying the demo! The account has been reset.
              </div>
            )}
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <p className="font-bold text-sm text-gray-900">Try the 20-minute Demo</p>
                <p className="text-xs text-gray-600 mt-0.5">Guided walkthrough of the entire platform</p>
              </div>
              {demoInUse ? (
                <span className="px-2 py-1 rounded text-xs font-bold bg-red-100 text-red-700 whitespace-nowrap" data-testid="demo-in-use-badge">
                  In use · {fmtRemaining(demoAvailability.remaining_seconds)}
                </span>
              ) : (
                <span className="px-2 py-1 rounded text-xs font-bold bg-emerald-100 text-emerald-700 whitespace-nowrap" data-testid="demo-available-badge">
                  Available
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-700 mb-3">
              <span className="font-mono bg-white px-2 py-1 rounded border">Demo@demo.com</span>
              <span className="text-gray-400">/</span>
              <span className="font-mono bg-white px-2 py-1 rounded border">Demo123</span>
            </div>
            <Button
              type="button"
              onClick={fillDemoCreds}
              disabled={demoInUse}
              className="w-full h-9 text-sm bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="demo-fill-creds-btn"
            >
              {demoInUse
                ? 'Demo busy — please wait your turn'
                : 'Use demo credentials'}
            </Button>
          </div>
        )}

        {/* LOCK SCREEN: when a demo is active, block the whole login form. */}
        {step === 'login' && demoInUse && (
          <div data-testid="login-locked-banner" className="mb-6 p-5 rounded-lg border-2 border-red-300 bg-gradient-to-br from-red-50 to-orange-50 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-red-600"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              <p className="font-bold text-sm text-red-800">Login Locked — Demo in Progress</p>
            </div>
            <p className="text-xs text-red-700 mb-3">
              Someone is currently running the demo. Please wait until their session ends.
            </p>
            <p className="text-xs font-mono font-bold text-red-900">
              Time left: {fmtRemaining(demoAvailability.remaining_seconds)}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {step === 'login' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john@example.com"
                  className="w-full"
                  required
                  disabled={loading}
                  autoComplete="email"
                  data-testid="login-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full"
                  required
                  disabled={loading}
                  autoComplete="current-password"
                  data-testid="login-password"
                />
              </div>
            </>
          )}

          {step === 'phone' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Phone Number</label>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 123 4567"
                className="w-full"
                required
                disabled={loading}
                autoFocus
                data-testid="login-phone"
              />
              <p className="text-xs text-gray-500 mt-2">We'll send a verification code to this number every time you log in.</p>
            </div>
          )}

          {step === '2fa' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Verification Code</label>
              <Input
                type="text"
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                className="w-full text-center text-2xl tracking-widest"
                required
                disabled={loading}
                autoFocus
                maxLength={6}
                data-testid="login-2fa-code"
              />
              <p className="text-xs text-gray-500 mt-2 text-center">
                Didn't receive a code?{' '}
                <button type="button" onClick={() => { setTwoFACode(''); handleSubmit(new Event('submit')); }} className="text-orange-600 underline">
                  Resend
                </button>
              </p>
            </div>
          )}

          <Button
            type="submit"
            disabled={loading || (step === '2fa' && twoFACode.length < 6) || (step === 'phone' && !phone)}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 text-lg"
            data-testid="login-submit"
          >
            {loading ? 'Verifying...' : step === '2fa' ? 'Verify Code' : step === 'phone' ? 'Save & Send Code' : 'Sign in'}
          </Button>

          {step !== 'login' && (
            <button
              type="button"
              onClick={handleBack}
              className="w-full text-sm text-gray-500 hover:text-gray-700 text-center"
            >
              Back to login
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default Login;
